import React from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Checkbox, TextField } from '@mui/material';
import { useSelector } from 'react-redux';
import { color } from '@/styles';
import { showMedicalContentSelector } from '@/redux/config/selfData';
import { IResidentCareRecord } from '@/types/api/traversal/getNursingHomeCareRecord';
import { numBetweenMinMax } from '@/utils/dataFormatUtil';
import { OutlineButton } from '@/components/outlineButton';
import { ResidentHeaderCareStatus } from '@/components/residentItemHeader/careStatus';
import { CareRecordButtonGroup } from '@/components/mui/radio/Radio';
import { CheckButton } from '@/components/mui/checkButton';
import { ResidentItemHeader } from '@/components/residentItemHeader';
import StoolKorokoro from '@/components/svg/stoolKorokoro';
import StoolHard from '@/components/svg/stoolHard';
import StoolLittleHard from '@/components/svg/stoolLittleHard';
import StoolMiddle from '@/components/svg/stoolMiddle';
import StoolSoft from '@/components/svg/stoolSoft';
import StoolLittleSoft from '@/components/svg/stoolLittleSoft';
import StoolWater from '@/components/svg/stoolWater';



interface Props {
  resident: IResidentCareRecord;
  onChange: (data: IBulkRecord) => void;
}

export interface IBulkRecord {
  residentId: number;
  urinatedToilet: boolean | null;
  toiletVolume: number | null;
  toiletType: string | null;
  padUrineVolumeType: string | null;
  padUrineVolume: number | null;
  urineLeakage: boolean | null;
  isUrgency: boolean | null;
  isLeakOutUrine: boolean | null;
  padType: string | null;
  defecationVolumeType?: string | null;
  hardnessType?: string | null;
  isLeakOutFeces: boolean | null;
  diaper: {
    useDefaultDiaper?: true | null;
    useDefaultPad?: true | null;
  };
}

const AlignCenter = css`
  display: flex;
  align-items: center;
`;

const ContentGap = css`
  gap: 4px;
`;

const Container = css`
  border-radius: 8px;
  background-color: ${color.white};
  color: ${color.dark_gray};
  margin-bottom: 16px;
  border: var(--border-card);
`;


const HeaderContents = css`
  display: flex;
  gap: 8px;
`;

const HeaderButtonContainer = css`
  justify-content: end;
  gap: 6px;
  margin-bottom: 4px;
  font-size: 12px;
`;

const BodyContainer = css`
  border-top: 1px solid ${color.border_gray};
  display: flex;
  padding: 16px;
  gap: 4px 28px;
  flex-wrap: wrap;
  justify-content: stretch;
`;

const RecordSection = css`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  flex-grow: 1;
  flex-shrink: 0;
`;

const SectionTitle = css`
  font-size: 18px;
  font-weight: bold;
  line-height: 1.2em;
  padding: 0 0 6px 4px;
`;

const VerticalSectionTitle = css`
  width: 40px;
  writing-mode: vertical-rl;
  margin-right: 8px;
  height: 92px;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 0.3em;
  border-right: 3px solid ${color.dark_gray};
  flex-shrink: 0;
`;

const SectionButtonContainer = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: space-between;
`;

const SectionUseContainer = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  max-width: 240px;
  height: 96px;
`;

const SectionButtonRow = css`
  flex-shrink: 0;
  height: 48px;
  width: 100%;
  min-width: 360px;
  flex-grow: 1;
  gap: 0 4px;
`;

const CheckboxRow = css`
  height: 32px;
`;

const CheckboxLabel = css`
  cursor: pointer;
  user-select: none;
`;

const CheckboxLabelDisabled = css`
  cursor: not-allowed;
  color: ${color.gray};
`;

const UrineLabelContainer = css`
  width: 88px;
  flex-shrink: 0;
`;

const UrineLabelIcon = css`
  width: 28px;
  height: 28px;
`;

const UrineLabelText = css`
  margin-left: 4px;
  font-size: 14px;
  color: ${color.gray};
  line-height: 1.2em;
  flex-shrink: 0;
  margin-right: 8px;
`;

export const BulkRecordItem = React.memo(({ resident, onChange }: Props) => {
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const initData: IBulkRecord = {
    residentId: resident.residentId,
    urinatedToilet: null,
    toiletVolume: null,
    toiletType: null,
    padUrineVolumeType: null,
    urineLeakage: null,
    isUrgency: null,
    isLeakOutUrine: null,
    padType: null,
    padUrineVolume: null,
    defecationVolumeType: null,
    hardnessType: null,
    isLeakOutFeces: null,
    diaper: {
      useDefaultDiaper: null,
      useDefaultPad: null,
    },
  };

  const [isCheckPant, setCheckPant] = React.useState<boolean>(false);
  const [isCheckPad, setCheckPad] = React.useState<boolean>(false);
  const [sendData, setSendData] = React.useState<IBulkRecord>(initData);
  const history = useHistory();
  const { t, i18n } = useTranslation();

  const normalizeRecords = React.useCallback((data: IBulkRecord): IBulkRecord => {
    const isDisabledToilet = !data.urinatedToilet;
    const isDisabledPad = !data.padUrineVolumeType || data.padUrineVolumeType === 'none';
    const isDisabledLeakUrine = isDisabledToilet && isDisabledPad;
    const isDisabledDefecation = !data.defecationVolumeType || data.defecationVolumeType === 'none';

    let next = data;

    if (isDisabledToilet && next.toiletVolume !== null) {
      next = { ...next, toiletVolume: null };
    }

    if (isDisabledPad && next.padUrineVolume !== null) {
      next = { ...next, padUrineVolume: null };
    }

    if (isDisabledLeakUrine && next.urineLeakage) {
      next = { ...next, urineLeakage: false };
    }

    if (isDisabledLeakUrine && next.isUrgency) {
      next = { ...next, isUrgency: false };
    }

    if (isDisabledLeakUrine && next.isLeakOutUrine) {
      next = { ...next, isLeakOutUrine: false };
    }

    if (isDisabledDefecation && next.hardnessType !== null) {
      next = { ...next, hardnessType: null };
    }

    if (isDisabledDefecation && next.isLeakOutFeces) {
      next = { ...next, isLeakOutFeces: false };
    }

    return next;
  }, []);

  const updateSendData = React.useCallback(
    (updater: (prev: IBulkRecord) => IBulkRecord) => {
      setSendData((prev) => normalizeRecords(updater(prev)));
    },
    [normalizeRecords],
  );

  // formControl
  const isDisabledHardnessTypeForm = React.useMemo(() => {
    return !sendData.defecationVolumeType || sendData.defecationVolumeType === 'none';
  }, [sendData]);

  const isDisabledToiletVolumeForm = React.useMemo(() => {
    return !sendData.urinatedToilet;
  }, [sendData.urinatedToilet]);

  const isDisabledPadUrineVolumeForm = React.useMemo(() => {
    return !sendData.padUrineVolumeType || sendData.padUrineVolumeType === 'none';
  }, [sendData.padUrineVolumeType]);

  const isDisabledLeakOutUrine = React.useMemo(() => {
    return isDisabledToiletVolumeForm && isDisabledPadUrineVolumeForm;
  }, [isDisabledToiletVolumeForm, isDisabledPadUrineVolumeForm]);

  const isDisabledLeakOutFeces = React.useMemo(() => {
    return !sendData.defecationVolumeType || sendData.defecationVolumeType === 'none';
  }, [sendData.defecationVolumeType]);

  const handleChangeIsUrinatedToilet = (data: string | number | boolean | null) => {
    updateSendData((prev) => {
      const isUrinatedToilet =
        data === 'true' ? true : data === 'false' ? false : null;
      return {
        ...prev,
        urinatedToilet: isUrinatedToilet,
      };
    });
  };

  const handleChangeToiletVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSendData((prev) => {
      return {
        ...prev,
        toiletVolume: numBetweenMinMax(+e.target.value, 0, 9999),
      };
    });
  };

  const handleChangePadUrineVolumeType = (data: string | number | boolean | null) => {
    updateSendData((prev) => {
      return {
        ...prev,
        padUrineVolumeType: data ? String(data) : null,
      };
    });
  };

  const handleChangePadUrineVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSendData((prev) => {
      return {
        ...prev,
        padUrineVolume: numBetweenMinMax(+e.target.value, 0, 9999),
      };
    });
  };

  const handleCheckUrineLeakage = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSendData((prev) => {
      return {
        ...prev,
        urineLeakage: e.target.checked,
      };
    });
  };

  const handleCheckIsUrgency = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSendData((prev) => {
      return {
        ...prev,
        isUrgency: e.target.checked,
      };
    });
  };

  const handleCheckLeakOutUrine = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSendData((prev) => {
      return {
        ...prev,
        isLeakOutUrine: e.target.checked,
      };
    });
  };

  const handleChangeDefecationVolumeType = (data: string | number | boolean | null) => {
    updateSendData((prev) => {
      return {
        ...prev,
        defecationVolumeType: data ? String(data) : null,
      };
    });
  };

  const handleChangeHardnessType = (data: string | number | boolean | null) => {
    updateSendData((prev) => {
      return {
        ...prev,
        hardnessType: data ? String(data) : null,
      };
    });
  };

  const handleCheckLeakOutFeces = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSendData((prev) => {
      return {
        ...prev,
        isLeakOutFeces: e.target.checked,
      };
    });
  };

  const handleClickPantButton = () => {
    setCheckPant(!isCheckPant);
    updateSendData((prev) => {
      return {
        ...prev,
        diaper: {
          useDefaultDiaper: !isCheckPant ? true : null,
          useDefaultPad: prev.diaper.useDefaultPad,
        },
      };
    });
  };

  const handleClickPadButton = () => {
    setCheckPad(!isCheckPad);
    updateSendData((prev) => {
      return {
        ...prev,
        diaper: {
          useDefaultDiaper: prev.diaper.useDefaultDiaper,
          useDefaultPad: !isCheckPad ? true : null,
        },
      };
    });
  };

  React.useEffect(() => {
    if (!Object.is(initData, sendData)) {
      onChange(sendData);
    }
  }, [sendData]);

  return (
    <>
      <div css={Container}>
        <ResidentItemHeader
          name={resident.residentName}
          unitName={resident.nursingHomeUnitName}
          roomName={resident.roomName}
          memo={resident.carePlanMemo}
          rightElement={
            <div css={HeaderContents}>
              <ResidentHeaderCareStatus resident={resident} />
              <div css={[AlignCenter, HeaderButtonContainer]}>
                <OutlineButton
                  color={color.green}
                  onClick={() => {
                    history.push(`residents/${resident.residentId}/care-record/new`);
                  }}
                >
                  {t('detailedRecordPlus', '詳細記録＋')}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    history.push(`residents/${resident.residentId}/graph`);
                  }}
                >
                  {t('detailOrEdit', '詳細 / 編集')}
                </OutlineButton>
              </div>
            </div>
          }
        />
        <div css={[BodyContainer]}>
          <div css={RecordSection}>
            <div css={[AlignCenter, SectionTitle]}>{t('urine', '排尿')}</div>
            <div css={SectionButtonContainer}>
              <div css={[AlignCenter, SectionButtonRow]}>
                <div css={[AlignCenter, UrineLabelContainer]}>
                  <img css={UrineLabelIcon} src='/images/bulkRecord/toilet.svg' alt={t('toilet', 'トイレ')} />
                  <span css={UrineLabelText}>{t('toilet', 'トイレ')}</span>
                </div>
                <CareRecordButtonGroup
                  data={[
                    { key: true, value: t('yes', '有') },
                    { key: false, value: t('no', '無') },
                  ]}
                  onChange={handleChangeIsUrinatedToilet}
                />
                <div css={[AlignCenter, ContentGap]}>
                  <TextField
                    label={t('toiletUrineAmount', 'トイレ排尿量')}
                    type={'number'}
                    value={sendData.toiletVolume ?? ''}
                    disabled={isDisabledToiletVolumeForm}
                    onChange={handleChangeToiletVolume}
                    size='small'
                    sx={{
                      width: 120,
                      '& .MuiInputLabel-root': { fontSize: 13, lineHeight: '1.7' },
                    }}
                  />
                  <span>ml</span>
                </div>
              </div>
              <div css={[AlignCenter, SectionButtonRow]}>
                <div css={[AlignCenter, UrineLabelContainer]}>
                  <img
                    css={UrineLabelIcon}
                    src='/images/bulkRecord/pad.svg'
                    alt={t('diaperOrPad', 'おむつ / パッド')}
                  />
                  <div css={UrineLabelText}>
                    <span>
                      {t('diaper', 'おむつ')}
                      <br />
                      {t('pad', 'パッド')}
                    </span>
                  </div>
                </div>
                <CareRecordButtonGroup
                  data={[
                    { key: 'large', value: t('large', '多'), icon: '/images/recordButtonIcon/urine-large.svg' },
                    { key: 'medium', value: t('medium', '中'), icon: '/images/recordButtonIcon/urine-medium.svg' },
                    { key: 'small', value: t('small', '少'), icon: '/images/recordButtonIcon/urine-small.svg' },
                    { key: 'none', value: t('none', '無'), icon: '/images/recordButtonIcon/urine-none.svg' },
                  ]}
                  onChange={handleChangePadUrineVolumeType}
                />
                <div css={[AlignCenter, ContentGap]}>
                  <TextField
                    label={t('padUrineAmount', 'パッド排尿量')}
                    type={'number'}
                    value={sendData.padUrineVolume ?? ''}
                    disabled={isDisabledPadUrineVolumeForm}
                    onChange={handleChangePadUrineVolume}
                    size='small'
                    sx={{
                      width: 120,
                      '& .MuiInputLabel-root': { fontSize: 13, lineHeight: '1.7' },
                    }}
                  />
                  <span>ml</span>
                </div>
              </div>
              <div css={[AlignCenter, SectionButtonRow, CheckboxRow]}>
                <div css={UrineLabelContainer} />
                {showMedicalContent && (
                  <div css={AlignCenter}>
                    <Checkbox
                      css={[]}
                      id={`urine-leakage-checkbox-${resident.residentId}`}
                      disabled={isDisabledLeakOutUrine}
                      checked={sendData.urineLeakage ?? false}
                      onChange={handleCheckUrineLeakage}
                    />
                    <label
                      css={[CheckboxLabel, isDisabledLeakOutUrine && CheckboxLabelDisabled]}
                      htmlFor={`urine-leakage-checkbox-${resident.residentId}`}
                    >
                      {t('leak', '尿漏れ')}
                    </label>
                  </div>
                )}
                {showMedicalContent && (
                  <div css={AlignCenter}>
                    <Checkbox
                      css={[]}
                      id={`is-urgency-checkbox-${resident.residentId}`}
                      disabled={isDisabledLeakOutUrine}
                      checked={sendData.isUrgency ?? false}
                      onChange={handleCheckIsUrgency}
                    />
                    <label
                      css={[CheckboxLabel, isDisabledLeakOutUrine && CheckboxLabelDisabled]}
                      htmlFor={`is-urgency-checkbox-${resident.residentId}`}
                    >
                      {t('isUrgency', '切迫感')}
                    </label>
                  </div>
                )}
                <div css={AlignCenter}>
                  <Checkbox
                    css={[]}
                    id={`leak-out-urine-checkbox-${resident.residentId}`}
                    disabled={isDisabledLeakOutUrine}
                    checked={sendData.isLeakOutUrine ?? false}
                    onChange={handleCheckLeakOutUrine}
                  />
                  <label
                    css={[CheckboxLabel, isDisabledLeakOutUrine && CheckboxLabelDisabled]}
                    htmlFor={`leak-out-urine-checkbox-${resident.residentId}`}
                  >
                    {t('clothesAndSheetsAreContaminated', '衣服・シーツ汚染あり')}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div css={RecordSection}>
            <div css={[AlignCenter, SectionTitle]}>{t('stool', '排便')}</div>
            <div css={SectionButtonContainer}>
              <div css={[AlignCenter, SectionButtonRow]}>
                <CareRecordButtonGroup
                  data={[
                    { key: 'large', value: t('large', '多'), icon: '/images/recordButtonIcon/stool-large.svg' },
                    { key: 'medium', value: t('medium', '中'), icon: '/images/recordButtonIcon/stool-medium.svg' },
                    { key: 'small', value: t('small', '少'), icon: '/images/recordButtonIcon/stool-small.svg' },
                    { key: 'bit', value: t('bit', '付'), icon: '/images/recordButtonIcon/stool-bit.svg' },
                    { key: 'none', value: t('none', '無'), icon: '/images/recordButtonIcon/stool-none.svg' },
                  ]}
                  onChange={handleChangeDefecationVolumeType}
                />
              </div>
              <div css={[AlignCenter, SectionButtonRow]}>
                <CareRecordButtonGroup
                  data={[
                    {
                      key: 'korokoro',
                      value: t('コロコロ', 'コロコロ'),
                      iconComponent: StoolKorokoro,
                      isNarrowText: true,
                    },
                    { key: 'hard', value: t('hard', '硬'), iconComponent: StoolHard },
                    { key: 'littleHard', value: t('やや硬', 'やや硬'), iconComponent: StoolLittleHard },
                    { key: 'middle', value: t('normal', '普通'), iconComponent: StoolMiddle },
                    { key: 'soft', value: t('soft', 'やや軟'), iconComponent: StoolSoft },
                    { key: 'littleSoft', value: t('mushy', '泥状'), iconComponent: StoolLittleSoft },
                    {
                      key: 'water',
                      value: t('waterly', '水様状'),
                      iconComponent: StoolWater,
                    },
                  ]}
                  newValue={sendData.hardnessType}
                  isDisabled={isDisabledHardnessTypeForm}
                  onChange={handleChangeHardnessType}
                />
              </div>
              <div css={[AlignCenter, SectionButtonRow, CheckboxRow]}>
                <div css={AlignCenter}>
                  <Checkbox
                    css={[]}
                    id={`leak-out-feces-checkbox-${resident.residentId}`}
                    disabled={isDisabledLeakOutFeces}
                    checked={sendData.isLeakOutFeces ?? false}
                    onChange={handleCheckLeakOutFeces}
                  />
                  <label
                    css={[CheckboxLabel, isDisabledLeakOutFeces && CheckboxLabelDisabled]}
                    htmlFor={`leak-out-feces-checkbox-${resident.residentId}`}
                  >
                    {t('clothesAndSheetsAreContaminated', '衣服・シーツ汚染あり')}
                  </label>
                </div>
              </div>
            </div>
          </div>
          {i18n.language.startsWith('ja') && (
            <div css={RecordSection}>
              <div css={[AlignCenter, SectionTitle]}>{t('use', '交換')}</div>
              <div css={SectionUseContainer}>
                <CheckButton onClick={handleClickPantButton}>{t('diaperPants', 'テープ/パンツ')}</CheckButton>
                <CheckButton onClick={handleClickPadButton}>{t('pad', 'パッド')}</CheckButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
});
